/**
 * The chests and the mimic in pixel art, after the GDD's mock-up of a chest's fall (page 20): a chest of 12 × 11
 * cells, gold edges and lock. The mimic has teeth along its lid, a red tongue and a glowing eye; it opens its mouth
 * wide when it bites (two eyes, a pink tongue, two rows of teeth) and it has legs (author's request), shown only while
 * it runs away hopping: stretched out on the way up, bent on the way down. Display side only.
 */
import { CHEST_H, CHEST_OPEN, CHEST_FALLING, CHEST_HOP, CHEST_SHORE, type Chest } from '@invaders/game';
import type { Camera } from './camera';

const PALETTE: Readonly<Record<string, string>> = {
  k: '#0d0a14', b: '#e0b040', c: '#8a5a34', e: '#fff1a8', y: '#ffe95a', w: '#f4f4ec', r: '#e04848', g: '#5a1a24',
  p: '#ff6a8a', l: '#5e361d',
};

const LID = ['..kkkkkkkk..', '.kbbbbbbbbk.'];
const BODY = ['kbccccccccbk', 'kbccccccccbk', 'kbbbbbbbbbbk', 'kkkkkkkkkkkk'];
const RIM = ['kkkkkeekkkkk', 'kbcckeekccbk', 'kbccckkcccbk'];

/** Grids, top to bottom. The art faces right (the mimic's feet trail behind it); mirrored to face left. */
const ART = {
  closed: [...LID, 'kbccccccccbk', 'kbccccccccbk', ...RIM, ...BODY],
  open: [...LID, 'kbccccccccbk', 'kbccccccccbk', '............', '............', '............', '............', ...RIM, ...BODY],
  mimic: [...LID, 'kbcccyccccbk', 'kbccccccccbk', 'kkwkweewkwkk', 'kbcrkeekccbk', 'kbcrckkcccbk', ...BODY],
  mimicLick: [...LID, 'kbcccyccccbk', 'kbccccccccbk', 'kkwkweewkwkk', 'kbcrkeekccbk', 'kbcrckkcccbk', 'kbcrccccccbk', ...BODY.slice(1)],
  mimicHalf: [...LID, 'kbcccyccccbk', 'kbccccccccbk', '.wgwgwgwgwg.', '.gggppppggg.', '.gwgwgwgwgw.', ...RIM, ...BODY],
  mimicBite: [
    ...LID, 'kbcyccccycbk', 'kbccccccccbk',
    '.wgwgwgwgwg.', '.gggggggggg.', '.gggggggggg.', '.gggggggggg.', '.gggppppggg.', '.gggppppggg.', '.gwgwgwgwgw.',
    ...RIM, ...BODY,
  ],
  legsBent: ['...l....l...', '..kk...kk...'],
  legsOut: ['...l....l...', '...l....l...', '..kk...kk...'],
} as const;
type Part = keyof typeof ART;

/** Where the glowing eyes are in each mimic grid (column, row). */
const EYES: Partial<Record<Part, readonly (readonly [number, number])[]>> = {
  mimic: [[5, 2]], mimicLick: [[5, 2]], mimicHalf: [[5, 2]], mimicBite: [[3, 2], [8, 2]],
};

const ART_W = 12;
const sprites = new Map<Part, HTMLCanvasElement>();

function sprite(part: Part): HTMLCanvasElement {
  let c = sprites.get(part);
  if (c) return c;
  const grid = ART[part];
  c = document.createElement('canvas');
  c.width = ART_W;
  c.height = grid.length;
  const g = c.getContext('2d')!;
  grid.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      const color = PALETTE[row[x]];
      if (!color) continue;
      g.fillStyle = color;
      g.fillRect(x, y, 1, 1);
    }
  });
  sprites.set(part, c);
  return c;
}

/** When each hopping mimic started to hop (it bit then: its mouth is wide open for a moment), and where it faces. */
const hopSince = new Map<number, number>();
const facing = new Map<number, number>();
const BITE_MS = 700;

/**
 * Draws a chest (its box's top-left corner at screen (sx, sy)): the art stands on the box's bottom edge, centred on it
 * (12 cells wide over a box of 10); a hopping mimic is raised on its legs.
 */
export function drawChestArt(ctx: CanvasRenderingContext2D, cam: Camera, c: Chest, sx: number, sy: number, now: number): void {
  const s = cam.scale;
  const left = sx - s;
  const bottom = sy + CHEST_H * s;
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  if (!c.mimic) {
    const part: Part = c.mode === CHEST_OPEN ? 'open' : 'closed';
    const img = sprite(part);
    ctx.drawImage(img, left, bottom - img.height * s, ART_W * s, img.height * s);
    if (c.mode === CHEST_OPEN) sparkles(ctx, s, left, bottom - img.height * s, now);
    ctx.restore();
    return;
  }

  // The mimic: which mouth and which legs.
  const hopping = c.mode === CHEST_HOP;
  if (hopping && !hopSince.has(c.id)) hopSince.set(c.id, now);
  if (!hopping) hopSince.delete(c.id);
  if (c.vx !== 0) facing.set(c.id, c.vx > 0 ? 1 : -1);
  const dir = facing.get(c.id) ?? 1;
  const hanging = c.mode === CHEST_FALLING || c.mode === CHEST_SHORE;
  const phase = (now + c.id * 1373) % 3200;
  let mouth: Part = 'mimic';
  if (hopping && now - hopSince.get(c.id)! < BITE_MS) mouth = 'mimicBite';
  else if (hopping) mouth = c.vy < 0 ? 'mimicHalf' : 'mimic';
  else if (!hanging && phase < 220) mouth = 'mimicHalf'; // it champs now and then
  else if (!hanging && phase > 1600 && phase < 1900) mouth = 'mimicLick';
  const body = sprite(mouth);
  const feet = hopping ? sprite(c.vy < 0 ? 'legsOut' : 'legsBent') : null;
  const top = bottom - (body.height + (feet?.height ?? 0)) * s;
  if (dir < 0) {
    // Mirrored around the art's middle.
    ctx.translate(left + ART_W * s, 0);
    ctx.scale(-1, 1);
    ctx.translate(-left, 0);
  }
  if (feet) ctx.drawImage(feet, left, top + body.height * s, ART_W * s, feet.height * s);
  ctx.drawImage(body, left, top, ART_W * s, body.height * s);
  // GDD: an eye that glows.
  const glow = 0.5 + 0.5 * Math.sin(now / 220 + c.id);
  for (const [ex, ey] of EYES[mouth] ?? []) {
    ctx.fillStyle = `rgba(255, 233, 90, ${0.18 + 0.22 * glow})`;
    ctx.fillRect(left + (ex - 1) * s, top + (ey - 1) * s, 3 * s, 3 * s);
    ctx.fillStyle = `rgb(255, ${200 + Math.round(55 * glow)}, ${60 + Math.round(120 * glow)})`;
    ctx.fillRect(left + ex * s, top + ey * s, s, s);
  }
  ctx.restore();
}

/** An open chest: gold glints twinkling between its lid and its body. */
function sparkles(ctx: CanvasRenderingContext2D, s: number, left: number, top: number, now: number): void {
  const spots = [[7, 4], [3, 5], [6, 5], [7, 5], [2, 6], [4, 6], [8, 6], [10, 6], [6, 7], [9, 4]];
  const frame = Math.floor(now / 160);
  ctx.fillStyle = '#ffe26a';
  for (let i = 0; i < spots.length; i++) {
    if ((i + frame) % 3 === 0) continue;
    ctx.fillRect(left + spots[i][0] * s, top + spots[i][1] * s, s, s);
  }
}
